import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/supabaseClient";
import { fetchProfileById, getCurrentSessionBundle, signInWithEmail, signOutUser } from "@/lib/blackrideOperations";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrate = async () => {
    setLoading(true);
    try {
      const bundle = await getCurrentSessionBundle();
      setSession(bundle.session);
      setUser(bundle.user);
      setProfile(bundle.profile);
      return bundle;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    hydrate();
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!nextSession?.user) {
        setSession(null);
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      setSession(nextSession);
      setUser(nextSession.user);
      setLoading(true);

      fetchProfileById(nextSession.user.id)
        .then((nextProfile) => {
          setProfile(nextProfile);
        })
        .catch((error) => {
          console.error('Auth profile fetch failed:', error);
          setProfile(null);
        })
        .finally(() => {
          setLoading(false);
        });
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const value = useMemo(() => ({
    session,
    user,
    profile,
    loading,
    refreshAuth: hydrate,
    signIn: async (email, password) => {
      setLoading(true);
      try {
        const authData = await signInWithEmail(email, password);
        const nextSession = authData.session ?? null;
        const nextUser = authData.user ?? nextSession?.user ?? null;
        const nextProfile = nextUser ? await fetchProfileById(nextUser.id) : null;

        setSession(nextSession);
        setUser(nextUser);
        setProfile(nextProfile);

        return { session: nextSession, user: nextUser, profile: nextProfile };
      } finally {
        setLoading(false);
      }
    },
    signOut: async () => {
      await signOutUser();
      setSession(null);
      setUser(null);
      setProfile(null);
    },
  }), [session, user, profile, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
